import { IS_PRIVATE } from '@/common/const/env'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'
import { HIDE_FUNC } from '@/common/const/hide-function-config'
import { IS_MING_TING_SUBJECT } from '@/project-subject/project-subject'
import { deepCopy, formatPath } from '@/utils/format'
import { CmRolePermKey } from '@/enum/role-perm'
import { PermissionFe } from '@/services/service-permission.service'

export enum SidebarActive {
  /** 继承 */
  inherit = 'inherit',
  /** 不选中 */
  none = 'none',
}

export const DEFAULT_ACTIVE = '/dashboard/overview'

export const LAST_NAV_KEY = 'LAST_NAV'

/** 侧边栏图标：对应 assets/nav-icon/ 下 iconfont 的 font_class，最终类名为 team-admin-nav-icon-{icon} */
export interface MenuItem {
  /** i18n key，用于侧边栏/顶部导航展示 */
  label: string
  path?: string
  vipOnly?: boolean
  isHide?: boolean
  noBreadcrumb?: boolean
  children?: MenuItem[]
  sidebarActive?: SidebarActive | string
  rolePermKey?: string
  /** 服务级权限 key（对应 PermissionFe 枚举值），日文专版下 hasPermission=false 时隐藏菜单 */
  servicePermissionKey?: string
  /** 是否页面固定、固定copyRight */
  pageFix?: boolean
  /** 功能描述 i18n key */
  describeKey?: string
  /** @deprecated 使用 describeKey */
  describe?: string
  /** 侧边栏图标类名（nav-icon 的 font_class，如 rocket、zhongduanguanli） */
  icon?: string
}

export const ORIGIN_MENU_LIST: MenuItem[] = [
  // {
  //   label: '总览',
  //   path: '/dashboard/home',
  // },
  {
    label: 'menu.overview',
    describeKey: 'menuDescribe.overview',
    path: '/dashboard/overview',
    icon: 'zonglan',
    /** 总览内容区自带头图与分区标题，不展示顶栏标题，避免重复 */
    noBreadcrumb: true,
  },
  {
    label: 'menu.safeProtection',
    rolePermKey: CmRolePermKey.safemonitor_examination,
    icon: 'shujuanquanguanli',
    children: [
      {
        label: 'menu.examinationStatus',
        describeKey: 'menuDescribe.examinationStatus',
        path: '/dashboard/examination-status',
        rolePermKey: CmRolePermKey.safemonitor_examination_stat,
        icon: 'shishizhuangtai',
      },
      {
        label: 'menu.virusScan',
        describeKey: 'menuDescribe.virusScan',
        path: '/dashboard/loaris-trojan-remover',
        rolePermKey: CmRolePermKey.safemonitor_virus_scan,
        servicePermissionKey: PermissionFe.virus_kill,
        icon: 'bingdu',
      },
      {
        label: 'menu.fileWhitelist',
        describeKey: 'menuDescribe.fileWhitelist',
        path: '/dashboard/file-whitelist',
        rolePermKey: CmRolePermKey.strategy_get_by_type,
        servicePermissionKey: PermissionFe.file_whitelist,
        isHide: HIDE_FUNC.fileWhitelist,
        icon: 'baimingdan',
      },
      {
        label: 'menu.vulnerabilityFix',
        describeKey: 'menuDescribe.vulnerabilityFix',
        path: '/dashboard/vulnerability-fix',
        rolePermKey: CmRolePermKey.leak_fix,
        servicePermissionKey: PermissionFe.vul_scan_fix,
        icon: 'loudongxiufu',
      },
      {
        label: 'menu.baselineScan',
        describeKey: 'menuDescribe.baselineScan',
        path: '/dashboard/base-line',
        rolePermKey: CmRolePermKey.baselinecheck_result_list,
        servicePermissionKey: PermissionFe.baseline_scan,
        icon: 'jixiansaomiao',
      },
      {
        label: 'menu.protectionStrategySetting',
        describeKey: 'menuDescribe.protectionStrategySetting',
        path: '/dashboard/group-policy',
        rolePermKey: CmRolePermKey.strategy_list_config,
        servicePermissionKey: PermissionFe.policy_settings_application,
        pageFix: true,
        icon: 'setting',
      },
      {
        label: 'menu.protectionStrategyDeploy',
        describeKey: 'menuDescribe.protectionStrategyDeploy',
        path: '/dashboard/policy-deploy',
        rolePermKey: CmRolePermKey.strategy_list_deploy,
        servicePermissionKey: PermissionFe.policy_settings_application,
        pageFix: true,
        icon: 'bushu',
      },
    ]
  },
  {
    label: 'menu.threatResponse',
    describeKey: 'menuDescribe.threatResponse',
    rolePermKey: CmRolePermKey.response,
    servicePermissionKey: PermissionFe.response_center,
    pageFix: true,
    icon: 'xiangyingchaoshi',
    children: [
      {
        label: 'menu.securityEvent',
        describeKey: 'menuDescribe.securityEvent',
        path: '/dashboard/security-event',
        servicePermissionKey: PermissionFe.security_check,
        rolePermKey: CmRolePermKey.safeevent_list,
        icon: 'anquanshijian',
        isHide: IS_JP_ONLY_BUILD
      },
      {
        label: 'menu.responseHandle',
        describeKey: 'menuDescribe.responseHandle',
        path: '/dashboard/response-handle',
        rolePermKey: CmRolePermKey.response_handle,
        pageFix: true,
        icon: 'xiangyingchaoshi',
      },
    ],
  },
  {
    label: 'menu.terminalAssets',
    rolePermKey: CmRolePermKey.terminal_manager,
    icon: 'terminal1',
    children: [
      {
        label: 'menu.terminalManage',
        describeKey: 'menuDescribe.terminalManage',
        path: '/dashboard/client',
        rolePermKey: CmRolePermKey.terminal_list,
        servicePermissionKey: PermissionFe.device_manage,
        icon: 'terminal1',
      },
      {
        label: 'menu.groupManage',
        describeKey: 'menuDescribe.groupManage',
        path: '/dashboard/group-manage',
        rolePermKey: CmRolePermKey.terminal_group_manager,
        servicePermissionKey: PermissionFe.device_manage,
        pageFix: true,
        icon: 'fenzuguanli1',
      },
      {
        label: 'menu.terminalDeploy',
        describeKey: 'menuDescribe.terminalDeploy',
        path: '/dashboard/client-alive',
        rolePermKey: CmRolePermKey.team_deploy_conf_get,
        icon: 'bushu',
      },
      {
        label: 'menu.terminalStatus',
        describeKey: 'menuDescribe.terminalStatus',
        path: '/dashboard/device-status',
        rolePermKey: CmRolePermKey.safemonitor_terminal_status_stat,
        servicePermissionKey: PermissionFe.terminal_online_status,
        icon: 'shishizhuangtai',
      },
      {
        label: 'menu.terminalHardware',
        describeKey: 'menuDescribe.terminalHardware',
        path: '/dashboard/hardware',
        rolePermKey: CmRolePermKey.terminal_hardware_list,
        servicePermissionKey: PermissionFe.terminal_hardware_info,
        icon: 'yingjian',
      },
      {
        label: 'menu.terminalDiscovery',
        describeKey: 'menuDescribe.terminalDiscovery',
        path: '/dashboard/terminal-discovery',
        servicePermissionKey: PermissionFe.client_find,
        rolePermKey: CmRolePermKey.client_find_discovered_list,
        icon: 'zhujifaxian',
      },

    ],
  },
  {
    label: 'menu.dataSecurity',
    rolePermKey: CmRolePermKey.data_security,
    icon: 'minganwenjian',
    children: [
      {
        label: 'menu.screenWatermark',
        describeKey: 'menuDescribe.screenWatermark',
        path: '/dashboard/screen-watermark',
        servicePermissionKey: PermissionFe.watermark_screen,
        rolePermKey: CmRolePermKey.watermark_strategy_list,
        pageFix: true,
        vipOnly: true,
        icon: 'shuiyin',
      },
      {
        label: 'menu.usbPolicy',
        describeKey: 'menuDescribe.usbPolicy',
        path: '/dashboard/usb-policy',
        servicePermissionKey: PermissionFe.usb_peripheral_control,
        rolePermKey: CmRolePermKey.peripheralcontrol_manager,
        vipOnly: true,
        icon: 'USB',
      },
      {
        label: 'menu.ransomwareIncident',
        describeKey: 'menuDescribe.ransomwareIncident',
        path: '/dashboard/ransomware-incident',
        servicePermissionKey: PermissionFe.ransomware_protect,
        rolePermKey: CmRolePermKey.ransomwareprotect,
        vipOnly: true,
        icon: 'lesuofanghu',
      },
      {
        label: 'menu.sensitiveFileScan',
        describeKey: 'menuDescribe.sensitiveFileScan',
        path: '/dashboard/sensitive-file-scanning',
        servicePermissionKey: PermissionFe.dlp_control,
        rolePermKey: CmRolePermKey.sensitivewords,
        vipOnly: true,
        icon: 'minganwenjian',
      },
    ]
  },
  {
    label: 'menu.internetProtection',
    rolePermKey: CmRolePermKey.portcontrol,
    icon: 'lesuofanghu',
    children: [
      {
        label: 'menu.portControlStatistics',
        describeKey: 'menuDescribe.portControlStatistics',
        path: '/dashboard/port-manage',
        servicePermissionKey: PermissionFe.port_control,
        rolePermKey: CmRolePermKey.portcontrol_port,
        vipOnly: true,
        icon: 'duankou1',
      },
      {
        label: 'menu.domainAccessRestriction',
        describeKey: 'menuDescribe.domainAccessRestriction',
        path: '/dashboard/access-restriction',
        servicePermissionKey: PermissionFe.internet_url_limit,
        rolePermKey: CmRolePermKey.portcontrol_port_terminal,
        vipOnly: true,
        icon: 'internet',
      },
      {
        label: 'menu.ipAccessRestriction',
        describeKey: 'menuDescribe.ipAccessRestriction',
        path: '/dashboard/ip-address-blocking',
        servicePermissionKey: PermissionFe.internet_ip_limit,
        rolePermKey: CmRolePermKey.ip_limit_list,
        vipOnly: true,
        icon: 'ip',
      },
      {
        label: 'menu.terminalTrafficStatus',
        describeKey: 'menuDescribe.terminalTrafficStatus',
        path: '/dashboard/terminal-traffic-state-list',
        servicePermissionKey: PermissionFe.flow_monitor,
        rolePermKey: CmRolePermKey.flow_control_status,
        icon: 'liuliang',
      },
      {
        label: 'menu.remoteLoginAuth',
        describeKey: 'menuDescribe.remoteLoginAuth',
        rolePermKey: CmRolePermKey.remote_login_authentication,
        path: '/dashboard/remote-authentication',
        servicePermissionKey: PermissionFe.remote_control,
        vipOnly: true,
        icon: 'icon_yuanchengdenglu',
      },
    ]
  },
  {
    label: 'menu.softwareManage',
    rolePermKey: CmRolePermKey.software_assets,
    icon: 'ruanjianguanli',
    children: [
      {
        label: 'menu.softwareList',
        describeKey: 'menuDescribe.softwareList',
        path: '/dashboard/software-manage',
        rolePermKey: CmRolePermKey.software_manager,
        servicePermissionKey: PermissionFe.software_manage,
        icon: 'liebiao',
      },
      {
        label: 'menu.softwareDispatcher',
        describeKey: 'menuDescribe.softwareDispatcher',
        path: '/dashboard/software-dispatcher',
        rolePermKey: CmRolePermKey.softmgr_soft_get,
        isHide: IS_JP_ONLY_BUILD,
        icon: 'ruanjianfenfa',
      },
      {
        label: 'menu.fileDispatch',
        describeKey: 'menuDescribe.fileDispatch',
        path: '/dashboard/file-dispatch',
        servicePermissionKey: PermissionFe.file_distribute,
        rolePermKey: CmRolePermKey.file_batch_get,
        vipOnly: true,
        icon: 'icon_wenjianfenfa',
      },
      {
        label: 'menu.softwareUsage',
        describeKey: 'menuDescribe.softwareUsage',
        path: '/dashboard/software-usage',
        rolePermKey: CmRolePermKey.softmgr_userecord,
        servicePermissionKey: PermissionFe.software_statistics,
        icon: 'shiyongtongji',
      },
      {
        label: 'menu.softwareDisabled',
        describeKey: 'menuDescribe.softwareDisabled',
        path: '/dashboard/software-disabled',
        servicePermissionKey: PermissionFe.soft_disable,
        rolePermKey: CmRolePermKey.softmgr_disable,
        vipOnly: true,
        icon: 'zongkaiguantingyong',
      },
      {
        label: 'menu.genuineSoftwareAuth',
        describeKey: 'menuDescribe.genuineSoftwareAuth',
        path: '/dashboard/genuine-software',
        servicePermissionKey: PermissionFe.soft_authorization,
        rolePermKey: CmRolePermKey.authenticsoft_list,
        vipOnly: true,
        isHide: IS_JP_ONLY_BUILD,
        icon: 'shouquanguanli',
      },
      {
        label: 'menu.genuineSoftwareUsage',
        describeKey: 'menuDescribe.genuineSoftwareUsage',
        path: '/dashboard/genuine-software-usage',
        rolePermKey: CmRolePermKey.authenticsoft_softmgr_userecord_soft_list,
        vipOnly: true,
        isHide: IS_JP_ONLY_BUILD,
        icon: 'shouquanruanjiantongji',
      },

      {
        label: 'menu.bootOptimize',
        describeKey: 'menuDescribe.bootOptimize',
        path: '/dashboard/boot-optimize',
        rolePermKey: CmRolePermKey.terminal_start_item,
        icon: 'kuaisu_jiasu',
        isHide: IS_JP_ONLY_BUILD,
      },
      {
        label: 'menu.popupIntercept',
        describeKey: 'menuDescribe.popupIntercept',
        path: '/dashboard/popup-intercept',
        servicePermissionKey: PermissionFe.popwindow_block,
        rolePermKey: CmRolePermKey.popupintercept,
        vipOnly: true,
        isHide: IS_JP_ONLY_BUILD,
        icon: 'guanggaolanjie',
      },
    ]
  },
  {
    label: 'menu.approvalCenter',
    describeKey: 'menuDescribe.approvalCenter',
    path: '/dashboard/approval-center',
    rolePermKey: CmRolePermKey.approval_list,
    pageFix: true,
    icon: 'shenpizhongxin1',
  },
  {
    label: 'menu.cascadeCenter',
    isHide: HIDE_FUNC.teamCascade,
    rolePermKey: CmRolePermKey.cascade,
    describeKey: 'menuDescribe.cascadeBranchOverview',
    path: '/dashboard/cascade-branch-overview',
    icon: 'duojiliandong',
  },
  {
    label: 'menu.logAudit',
    rolePermKey: CmRolePermKey.logmanage,
    icon: 'rizhichaxun',
    children: [
      {
        label: 'menu.warningLog',
        describeKey: 'menuDescribe.warningLog',
        path: '/dashboard/warning-log',
        rolePermKey: CmRolePermKey.logmanage_alert_history_list,
        pageFix: true,
        icon: 'quanbuyujingxinxi',
      },
      {
        label: 'menu.virusLog',
        describeKey: 'menuDescribe.virusLog',
        path: '/dashboard/virus-log',
        rolePermKey: CmRolePermKey.logmanage_virus_stat,
        pageFix: true,
        icon: 'bingdu',
      },
      {
        label: 'menu.hardwareModifyLog',
        describeKey: 'menuDescribe.hardwareModifyLog',
        path: '/dashboard/hardware-modify-log',
        rolePermKey: CmRolePermKey.logmanage_hardware_change_list,
        pageFix: true,
        icon: 'yingjian',
      },
      {
        label: 'menu.softwareModifyLog',
        describeKey: 'menuDescribe.softwareModifyLog',
        path: '/dashboard/software-modify-log',
        rolePermKey: CmRolePermKey.logmanage_software_change_list,
        pageFix: true,
        icon: 'liebiao',
      },
      {
        label: 'menu.inOutLog',
        describeKey: 'menuDescribe.inOutLog',
        path: '/dashboard/in-out-log',
        rolePermKey: CmRolePermKey.logmanage_join_record_list,
        pageFix: true,
        icon: 'a-churu1',
      },
      {
        label: 'menu.softDisabledLog',
        describeKey: 'menuDescribe.softDisabledLog',
        path: '/dashboard/soft-disabled-log',
        rolePermKey: CmRolePermKey.softmgr_teamsoft_disable_log,
        pageFix: true,
        icon: 'zongkaiguantingyong',
      },
      {
        label: 'menu.genuineSoftwareLog',
        describeKey: 'menuDescribe.genuineSoftwareLog',
        path: '/dashboard/genuine-software-log',
        rolePermKey: CmRolePermKey.softmgr_teamsoft_auth_disable_log,
        pageFix: true,
        isHide: IS_JP_ONLY_BUILD,
        icon: 'weiquanzhong',
      },
      {
        label: 'menu.domainStrategyLog',
        describeKey: 'menuDescribe.domainStrategyLog',
        path: '/dashboard/internet-restriction',
        rolePermKey: CmRolePermKey.terminal_net_limit_stat,
        pageFix: true,
        icon: 'internet',
      },
      {
        label: 'menu.outboundLog',
        describeKey: 'menuDescribe.outboundLog',
        path: '/dashboard/outbound-control-log',
        rolePermKey: CmRolePermKey.sensitivefilesend_send_file_record,
        pageFix: true,
        isHide: IS_JP_ONLY_BUILD,
        icon: 'waifawenjian',
      },
      {
        label: 'menu.troubleshootLog',
        describeKey: 'menuDescribe.troubleshootLog',
        path: '/dashboard/trouble-shoot-log',
        rolePermKey: CmRolePermKey.terminal_order_get_logs_task_list,
        pageFix: true,
        icon: 'rizhichaxun',
      },
      {
        label: 'menu.ipRestrictionLog',
        describeKey: 'menuDescribe.ipRestrictionLog',
        path: '/dashboard/ip-address-blocking-log',
        rolePermKey: CmRolePermKey.terminal_net_ip_limit_list,
        pageFix: true,
        icon: 'ip',
      },
      {
        label: 'menu.remoteLoginLog',
        describeKey: 'menuDescribe.remoteLoginLog',
        path: '/dashboard/remote-login-log',
        rolePermKey: CmRolePermKey.terminal_remote_login_log,
        pageFix: true,
        icon: 'icon_yuanchengdenglu',
      },
      {
        label: 'menu.messagePushLog',
        describeKey: 'menuDescribe.messagePushLog',
        path: '/dashboard/message-push',
        rolePermKey: CmRolePermKey.terminal_order_command_stat,
        pageFix: true,
        icon: 'zhilingxiafa',
      },
      {
        label: 'menu.adminOperationLog',
        describeKey: 'menuDescribe.adminOperationLog',
        path: '/dashboard/admin-log',
        rolePermKey: CmRolePermKey.logmanage_admin_operation_list,
        pageFix: true,
        icon: 'guanliyuanguanli',
      }
    ]
  },
  {
    label: 'menu.settings',
    rolePermKey: CmRolePermKey.setting,
    icon: 'setting',
    children: [
      {
        label: 'menu.assetRegistration',
        describeKey: 'menuDescribe.assetRegistration',
        path: '/dashboard/asset-registration',
        rolePermKey: CmRolePermKey.asset_registration,
        pageFix: true,
        icon: 'zichandengji',
      },

      {
        label: 'menu.mailNotify',
        describeKey: 'menuDescribe.mailNotify',
        path: '/dashboard/mail-setting',
        rolePermKey: CmRolePermKey.notice_mail_strategy_get,
        isHide: HIDE_FUNC.emailNotify,
        pageFix: true,
        icon: 'xinxiaoxiyoujian',
      },
      {
        label: 'menu.adminList',
        describeKey: 'menuDescribe.adminList',
        path: '/dashboard/admin-account-settings',
        rolePermKey: CmRolePermKey.team_admin_list,
        pageFix: true,
        isHide: IS_PRIVATE || IS_JP_ONLY_BUILD,
        icon: 'guanliyuanguanli',
      },
      {
        label: 'menu.adminAccount',
        describeKey: 'menuDescribe.adminAccount',
        path: '/dashboard/admin-account-settings',
        rolePermKey: CmRolePermKey.team_admin,
        pageFix: true,
        isHide: !IS_PRIVATE && !IS_JP_ONLY_BUILD,
        icon: 'guanliyuanguanli',
      },
      {
        label: 'menu.inviteAdmin',
        describeKey: 'menuDescribe.inviteAdmin',
        path: '/dashboard/invite-list',
        rolePermKey: CmRolePermKey.team_admin_invite,
        isHide:IS_JP_ONLY_BUILD,
        pageFix: true,
        icon: 'yaoqing',
      },
      {
        label: 'menu.versionUpdate',
        describeKey: 'menuDescribe.versionUpdate',
        path: '/dashboard/version-update',
        rolePermKey: CmRolePermKey.upgrade_version,
        isHide: HIDE_FUNC.update__version,
        icon: 'rocket',
      },
      {
        label: 'menu.virusLibUpdate',
        describeKey: 'menuDescribe.virusLibUpdate',
        path: '/dashboard/cloud-feature-offline-library',
        rolePermKey: CmRolePermKey.sample_update_update_state,
        isHide: HIDE_FUNC.update__cloudLibrary,
        icon: 'bingdu',
      },
      {
        label: 'menu.systemConfig',
        describeKey: 'menuDescribe.systemConfig',
        path: '/dashboard/system-config',
        rolePermKey: CmRolePermKey.teamconfig_get,
        pageFix: true,
        icon: 'setting',
      },
      {
        label: 'menu.serverStatus',
        describeKey: 'menuDescribe.serverStatus',
        path: '/dashboard/run-status',
        rolePermKey: CmRolePermKey.metric_server_status,
        isHide: HIDE_FUNC.runStatus,
        icon: 'shishizhuangtai',
      },
      {
        label: 'menu.ldapSyncSetting',
        describeKey: 'menuDescribe.ldapSyncSetting',
        path: '/dashboard/ldap-sync-setting',
        rolePermKey: CmRolePermKey.terminal_group_ldap_config,
        isHide: !IS_PRIVATE,
        icon: 'setting',
      },
    ]
  },
  {
    label: 'menu.myService',
    rolePermKey: CmRolePermKey.my_service,
    icon: 'wodedingyue',
    children: [
      {
        label: 'menu.subscribeMarket',
        describeKey: 'menuDescribe.subscribeMarket',
        path: '/dashboard/service-subscribe',
        rolePermKey: CmRolePermKey.permission_order_get_prices,
        isHide: !IS_MING_TING_SUBJECT || HIDE_FUNC.service__subscribe || IS_JP_ONLY_BUILD,
        noBreadcrumb: true,
        icon: 'shichang-caigou-nor',
      },
      {
        label: 'menu.mySubscribe',
        describeKey: 'menuDescribe.mySubscribe',
        path: '/dashboard/my-subscribe',
        rolePermKey: CmRolePermKey.permission_order_service_list,
        isHide: IS_JP_ONLY_BUILD,
        icon: 'wodedingyue',
      },
      {
        label: 'menu.myOrder',
        describeKey: 'menuDescribe.myOrder',
        path: '/dashboard/order-history',
        rolePermKey: CmRolePermKey.permission_order_order_history,
        isHide: !IS_MING_TING_SUBJECT || IS_JP_ONLY_BUILD,
        icon: 'dingdan01',
      },
      {
        label: 'menu.contractInfo',
        describeKey: 'menuDescribe.contractInfo',
        path: '/dashboard/contract-info',
        rolePermKey: CmRolePermKey.permission_order_service_list,
        isHide: !IS_JP_ONLY_BUILD,
        icon: 'hetongxinxi',
      },
    ]
  },

  /**
   * 没有自己导航选中的页面
   */
  {
    label: '',
    children: [
      {
        label: 'menu.softwareDisabled',
        describeKey: 'menuDescribe.softwareDisabledEdit',
        path: '/dashboard/software-disabled-edit',
        servicePermissionKey: PermissionFe.soft_disable,
        rolePermKey: CmRolePermKey.softmgr_disable,
        sidebarActive: '/dashboard/software-disabled',
        icon: 'zongkaiguantingyong',
      },
      {
        label: 'menu.processControlEdit',
        describeKey: 'menuDescribe.processControlEdit',
        path: '/dashboard/process-control-edit',
        servicePermissionKey: PermissionFe.proc_control,
        rolePermKey: CmRolePermKey.processdisable_list,
        pageFix: true,
        sidebarActive: '/dashboard/process-control',
        icon: 'yunhangkongzhi',
      },
      {
        label: 'menu.screenWatermarkEdit',
        describeKey: 'menuDescribe.screenWatermarkEdit',
        path: '/dashboard/screen-watermark-edit',
        servicePermissionKey: PermissionFe.watermark_screen,
        rolePermKey: CmRolePermKey.watermark_strategy_list,
        sidebarActive: '/dashboard/screen-watermark',
        pageFix: true,
        icon: 'shuiyin',
      },
      {
        label: 'menu.subscribeDetails',
        describeKey: 'menuDescribe.subscribeDetails',
        path: '/dashboard/subscribe-details',
        rolePermKey: CmRolePermKey.permission_order_get_prices,
        sidebarActive: '/dashboard/service-subscribe',
        isHide: !IS_MING_TING_SUBJECT || HIDE_FUNC.service__subscribe || IS_JP_ONLY_BUILD,
        icon: 'shichang-caigou-nor',
      },
      {
        label: 'menu.buyMore',
        describeKey: 'menuDescribe.buyMore',
        path: '/dashboard/buy-more',
        rolePermKey: CmRolePermKey.permission_order_get_prices,
        sidebarActive: '/dashboard/service-subscribe',
        isHide: !IS_MING_TING_SUBJECT || HIDE_FUNC.service__subscribe || IS_JP_ONLY_BUILD,
        icon: 'shichang-caigou-nor',
      },
      {
        label: 'menu.aiAssistantDetail',
        describeKey: 'menuDescribe.aiAssistantDetail',
        path: '/dashboard/ai-assistant-detail',
        rolePermKey: CmRolePermKey.permission_order_get_prices,
        sidebarActive: '/dashboard/service-subscribe',
        noBreadcrumb: true,
        isHide: !IS_MING_TING_SUBJECT || HIDE_FUNC.service__subscribe || IS_JP_ONLY_BUILD,
        icon: 'shichang-caigou-nor',
      },
    ],
  }
]

export const MENU_LIST = deepFilterMenu({ isExclude: e => e.isHide })

/**
 * 深度过滤菜单列表
 * @param params - 过滤参数
 * @returns 过滤后的菜单列表
 */
export function deepFilterMenu(params: {
  isExclude: (menu: MenuItem) => boolean
  menuList?: MenuItem[]
}): MenuItem[] {
  const menuList = params.menuList ? deepCopy(params.menuList) : deepCopy(ORIGIN_MENU_LIST)

  const deep = (menuList) => {
    return menuList?.filter(item => {
      if (item.children) {
        item.children = deep(item.children)
      }
      return !params.isExclude(item)
    })
  }
  return deep(menuList)
}

/**
 * 检查当前路径是否不存在于菜单列表中
 * @param params - 检查参数
 * @returns 如果路径不存在则返回true，否则返回false
 */
export function isPathNotExists(params: {
  currentPath: string
  isExclude: (menu: MenuItem) => boolean
  menuList?: MenuItem[]
}): boolean {
  const { currentPath } = params
  const formattedTargetPath = formatPath(currentPath)
  const menuList = deepFilterMenu({
    isExclude: params.isExclude,
    menuList: params.menuList,
  })

  const hasPath = (items: MenuItem[]): boolean => {
    if (!items) return false

    return items.some((item) => {
      if (formatPath(item.path) === formattedTargetPath) {
        return true
      }
      return hasPath(item.children)
    })
  }

  // 不在NavMenu中配置的，视为存在
  const inNavMenu = hasPath(deepCopy(ORIGIN_MENU_LIST))
  if (!inNavMenu) return false

  return !hasPath(menuList)
}
